import { getGPUTier } from '@pmndrs/detect-gpu';
import { computed, ref, shallowRef } from 'vue';

/**
 * Site-wide GPU classification via {@link https://github.com/pmndrs/detect-gpu @pmndrs/detect-gpu}.
 * Started from `gpu-tier.client` plugin; call {@link runGpuTierProbe} with `glContext` after the
 * Three renderer exists for a more accurate match (e.g. default layout / sandbox `onRenderSetup`).
 */

export const gpuTierStatus = ref(/** @type {'idle' | 'pending' | 'ready' | 'error'} */ ('idle'));
export const gpuTierSnapshot = shallowRef(null);
export const gpuTierError = shallowRef(null);

let probePromise = null;

/**
 * detect-gpu mobile tiers are coarse; demote older phones (pre–A16-class) one step so
 * A16+ (e.g. iPhone 15 / 14 Pro) keep the library’s top tier. Apple M-series iPads: no demotion.
 * Non-Apple / unparseable mobile GPU strings: conservative demotion.
 */
export const shouldDemoteMobileGpuTier = (result) => {
    if (!result?.isMobile) return false;
    const gpu = (result.gpu || '').toLowerCase();
    if (/apple m\d/.test(gpu)) return false;
    const m = gpu.match(/\ba(\d+)/);
    if (!m) return true;
    const gen = parseInt(m[1], 10);
    return gen < 16;
};

/** Applies {@link shouldDemoteMobileGpuTier} to `result.tier` (shallow copy if tier changes). */
export const normalizeGpuTierResult = (result) => {
    if (!result || typeof result.tier !== 'number') return result;
    let { tier } = result;
    if (shouldDemoteMobileGpuTier(result) && tier > 1) tier -= 1;
    if (tier === result.tier) return result;
    return { ...result, tier };
};

/**
 * Run (or await) the GPU tier probe. Concurrent calls without `force` share one in-flight promise.
 * With `force: true`, waits for any current probe to finish, then runs again (e.g. with `glContext`).
 */
export const runGpuTierProbe = async ({ glContext, force = false } = {}) => {
    if (import.meta.server) return null;

    if (probePromise && force) await probePromise;

    if (gpuTierStatus.value === 'ready' && !force) return gpuTierSnapshot.value;
    if (probePromise && !force) return probePromise;

    gpuTierStatus.value = 'pending';
    gpuTierError.value = null;

    probePromise = (async () => {
        try {
            const r = await getGPUTier({ glContext });
            const normalized = normalizeGpuTierResult(r);
            gpuTierSnapshot.value = normalized;
            gpuTierStatus.value = 'ready';
            return normalized;
        } catch (err) {
            gpuTierError.value = err;
            gpuTierStatus.value = 'error';
            gpuTierSnapshot.value = normalizeGpuTierResult({
                type: 'ERROR',
                tier: 2,
                isMobile: undefined,
            });
            return gpuTierSnapshot.value;
        } finally {
            probePromise = null;
        }
    })();

    return probePromise;
};

/**
 * Read global GPU tier state (safe on server — refs stay at initial values).
 */
export const useGpuTier = () => {
    const tier = computed(() =>
        typeof gpuTierSnapshot.value?.tier === 'number' ? gpuTierSnapshot.value.tier : null
    );
    const type = computed(() => gpuTierSnapshot.value?.type ?? null);
    const isMobile = computed(() => gpuTierSnapshot.value?.isMobile);
    const fps = computed(() => gpuTierSnapshot.value?.fps);
    const gpu = computed(() => gpuTierSnapshot.value?.gpu);
    const isReady = computed(() => gpuTierStatus.value === 'ready');

    return {
        status: gpuTierStatus,
        result: gpuTierSnapshot,
        error: gpuTierError,
        tier,
        type,
        isMobile,
        fps,
        gpu,
        isReady,
        runGpuTierProbe,
    };
};
